import React from "react";
import PropTypes from "prop-types";

import {
  Container,
  Typography,
  Box,
  Stack,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import Iconify from "src/components/iconify";
import TenantSettings from "src/sections/@dashboard/settings/TenantSettings";
import NewPasswordSettings from "src/sections/@dashboard/settings/NewPasswordSettings";
import FAQs from "src/sections/@dashboard/settings/FAQs";
import useAuth from "src/hooks/useAuth";
import {
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { settingsNavList } from "../sections/@dashboard/settings/settingsNav";
import BillingSettings from "../sections/@dashboard/settings/BillingSettings";
import ModuleSettings from "../sections/@dashboard/settings/ModuleSettings";
import { Helmet } from "react-helmet-async";
import BankingSettings from "../sections/@dashboard/settings/Banking/BankingSettings";
import MailSettings from "src/sections/@dashboard/settings/MailSettings";
import MailConfigurationSettings from "src/sections/@dashboard/settings/MailConfigurationSettings";
import MailTerminationTemplateSettings from "src/sections/@dashboard/settings/MailTerminationTemplateSettings";
import MailRegistrationTemplateSettings from "src/sections/@dashboard/settings/MailRegistrationTemplateSettings";
import MailSelfRegReceiptTemplateSettings from "src/sections/@dashboard/settings/MailSelfRegReceiptTemplateSettings";
import MailSelfRegApprovalTemplateSettings from "src/sections/@dashboard/settings/MailSelfRegApprovalTemplateSettings";
import MailSelfRegRejectionTemplateSettings from "src/sections/@dashboard/settings/MailSelfRegRejectionTemplateSettings";
import MailRegistrationLinkTemplateSettings from "src/sections/@dashboard/settings/MailRegistrationLinkTemplateSettings";
import RolePreviewControl from "src/features/rolePreview/RolePreviewControl";
import ShopDeliverySettings from "src/sections/@dashboard/settings/ShopDeliverySettings";
import { Icon } from "@iconify/react";

// Centered nav item styling: card-like spacing, border, and shadow for a friendly landing view.
const centeredNavItemSx = {
  maxWidth: "100%",
  mb: 1.5,
  borderRadius: 2,
  px: 2.5,
  py: 1.75,
  boxShadow: "0 12px 24px -18px rgba(0, 0, 0, 0.4)",
  border: "1px solid rgba(0, 0, 0, 0.12)",
};

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isRootSettings = /\/dashboard\/settings\/?$/.test(location.pathname);

  return (
    <Container maxWidth="ml">
      <Helmet>
        <title> MA | Einstellungen</title>
      </Helmet>
      <Typography variant="h3" component="h1" gutterBottom>
        Einstellungen
      </Typography>
      {isRootSettings ? (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ maxWidth: 640, mb: 3 }}
          >
            Wähle eine Einstellung aus, um Details zu sehen und Änderungen
            vorzunehmen.
          </Typography>
          <List
            component="nav"
            aria-label="settings navigation"
            sx={{ width: "100%", maxWidth: 560 }}
          >
            {settingsNavList.map((item) => (
              <SettingsNavItem
                key={item.path}
                item={item}
                isAdmin={isAdmin}
                variant="centered"
              />
            ))}
          </List>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Button
              onClick={() => navigate("/dashboard/settings")}
              variant="outlined"
              sx={{ m: 0 }}
              startIcon={<Icon width="16" icon="mdi:arrow-left" />}
            >
              <Typography display={"inline"} variant="button" sx={{ mb: 0 }}>
                Zurück
              </Typography>
            </Button>
          </Box>
          <Box>
            <Routes>
              {isAdmin && (
                <>
                  <Route path="tenant/*" element={<TenantSettings />} />
                  <Route path="banking" element={<BankingSettings />} />
                  <Route path="modules" element={<ModuleSettings />} />
                  <Route path="payment" element={<BillingSettings />} />
                  <Route path="mail" element={<MailSettings />} />
                  <Route
                    path="mail/config"
                    element={<MailConfigurationSettings />}
                  />
                  <Route
                    path="mail/termination-template"
                    element={<MailTerminationTemplateSettings />}
                  />
                  <Route
                    path="mail/registration-template"
                    element={<MailRegistrationTemplateSettings />}
                  />
                  <Route
                    path="mail/selfreg-receipt-template"
                    element={<MailSelfRegReceiptTemplateSettings />}
                  />
                  <Route
                    path="mail/selfreg-approval-template"
                    element={<MailSelfRegApprovalTemplateSettings />}
                  />
                  <Route
                    path="mail/selfreg-rejection-template"
                    element={<MailSelfRegRejectionTemplateSettings />}
                  />
                  <Route
                    path="mail/registration-link-template"
                    element={<MailRegistrationLinkTemplateSettings />}
                  />
                  <Route
                    path="shop-delivery"
                    element={<ShopDeliverySettings />}
                  />
                  <Route
                    path="role-preview"
                    element={<RolePreviewControl />}
                  />
                </>
              )}
              <Route path="password" element={<NewPasswordSettings />} />
              <Route path="faq" element={<FAQs />} />
            </Routes>
            <Outlet />
          </Box>
        </Stack>
      )}
    </Container>
  );
};

export default SettingsPage;

// ----------------------------------------------------------------------

SettingsNavItem.propTypes = {
  item: PropTypes.object,
  variant: PropTypes.string,
};

function SettingsNavItem({ item, isAdmin, variant = "default" }) {
  const { title, path, icon, role } = item;

  if (role === "Admin" && !isAdmin) {
    return null;
  }

  return (
    <ListItemButton
      component={NavLink}
      to={path}
      sx={{
        "&.active": {
          color: "text.primary",
          fontWeight: "fontWeightBold",
        },
        maxWidth: variant === "centered" ? "100%" : 300,
        ...(variant === "centered" ? centeredNavItemSx : null),
      }}
    >
      <ListItemIcon sx={{ minWidth: 25 }}>
        <Iconify icon={icon} />
      </ListItemIcon>

      <ListItemText disableTypography primary={title} />
    </ListItemButton>
  );
}
